import React, { useEffect } from 'react'

import { useTranslation } from 'language/translator'

import Alert from 'components_new/atoms/Alert'
import Button from 'components_new/atoms/Button'

import { useCompanyGroupActions } from 'redux/actions/CompanyGroups'
import { useCompanyStructureSyncStatus } from 'redux/hooks/CompanyGroups'
import { useIsManagementCompany } from 'redux/hooks/Customer'

const POLL_INTERVAL_MS = 15000

/**
 * Warns a management company when the company structure (customers,
 * companies) has changed after the latest ontology run, i.e. the data
 * shown in the platform is out of date, and offers to rerun the
 * ontology job. While a rerun started from here is in progress the
 * alert stays visible in a loading state, polling until the run
 * finishes.
 */
const CompanyStructureSyncAlert = () => {
  const { t } = useTranslation()

  const isManagementCompany = useIsManagementCompany()
  const syncStatus = useCompanyStructureSyncStatus()

  const { tryGetCompanyStructureSyncStatus, tryRerunOntology } =
    useCompanyGroupActions()

  const showStale = syncStatus.stale
  const showRunning =
    !syncStatus.stale && syncStatus.running && syncStatus.triggered
  const loading =
    syncStatus.starting || syncStatus.running || syncStatus.triggered

  useEffect(() => {
    if (isManagementCompany && !syncStatus.fetched) {
      tryGetCompanyStructureSyncStatus()
    }
  }, [isManagementCompany])

  // Poll while a run we care about is in progress, so the alert
  // resolves itself when the job finishes.
  useEffect(() => {
    if (!isManagementCompany || !(showRunning || (showStale && loading))) {
      return
    }

    const interval = setInterval(
      () => tryGetCompanyStructureSyncStatus(),
      POLL_INTERVAL_MS
    )

    return () => clearInterval(interval)
  }, [isManagementCompany, showRunning, showStale, loading])

  if (!isManagementCompany || (!showStale && !showRunning)) {
    return null
  }

  const minutes = syncStatus.expectedDurationMinutes
  const estimate =
    minutes !== null
      ? ' ' +
        t('company_groups', 'takes_about_{{minutes}}_min', false, {
          minutes: Math.max(1, Math.round(minutes)).toString()
        })
      : ''

  const dataProductId = syncStatus.ontologyDataProductId

  return (
    <Alert
      action={
        dataProductId ? (
          <Button
            color={showStale ? 'warning' : 'info'}
            loading={loading}
            loadingTitle={t('company_groups', 'rerun_ontology_running')}
            onClick={() => tryRerunOntology(dataProductId)}
            size="small"
            variant="text"
          >
            {t('company_groups', 'rerun_ontology')}
          </Button>
        ) : undefined
      }
      severity={showStale ? 'warning' : 'info'}
      sx={{ alignItems: 'center', mb: 2 }}
    >
      {showStale
        ? t('company_groups', 'structure_changed_warning') + estimate
        : t('company_groups', 'rerun_in_progress') + estimate}
    </Alert>
  )
}

export default CompanyStructureSyncAlert
